import * as React from "react";
import { observer } from "mobx-react";
import { Layout } from "antd";
import classNames from "classnames";
import { headerStore, userInfoStore } from "Stores";
// import { RouteWithTitle } from "Components/Utils/RouteWithTitle";
import { titleStore } from "Stores/TitleStore";
import { projectsDataStore } from "Stores/ProjectsStore";
import { Loader } from "Components/General/Loader";
import { Sidebar } from "Components/Core/Sidebar/Sidebar";
import { Disclaimer } from "Pages/Project/Disclaimer/Disclaimer";
import { withTranslation, WithTranslation } from "react-i18next";
import { SwitchWith404 } from "Config/Router";
import { ErrorBoundary } from "Components/General/ErrorBoundary";
import { contactsStore } from "Stores/ContactsStore";
import { QA } from "Pages/Project/QA/QA";
import { LoadAsync } from "Components/General/Loadable";
import { clearAllStores } from "Stores/RESTStore/CoreStore";
import { I18N_NS } from "Config/i18n";
import { groupListStore } from "./Groups/Stores/GroupsStore";
import { WebDAVModal } from "Components/FileDetails/WebDAVModal";
import { printJobSettingsStore } from "./Settings/Stores/PrintJobStore";
import { UserVerification } from "./UserVerification";
// import { FileUpload } from "./Documents/FileUpload/FileUpload";
import { ConfirmPassword } from "./ConfirmPassword";
import { FileDownload } from "./Documents/FileDownload/FileDownload";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { sidebarStore } from "Components/Core/Sidebar/SidebarStore";
import { TaskAccessRightsModal } from "./Tasks/Components/TaskAccessRightsModal";
import { ObjectsAndData } from "./ObjectsAndData/ObjectsAndData";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { Navigate, Route } from "react-router-dom";
import { RouteWithTitle } from "Components/Utils/RouteWithTitle";
import { getStarterModulePath } from "Stores/helpers";
import { StaCallback } from "./Sta/StaCallback";

const TasksRouter = LoadAsync(() => import(/* webpackChunkName: "tasks" */ "Pages/Project/Tasks/TasksRouter"));
const Reports = LoadAsync(() => import(/* webpackChunkName: "reports" */ "Pages/Project/Reports/Reports"));
const GroupsRouter = LoadAsync(() => import(/* webpackChunkName: "groups" */ "Pages/Project/Groups/GroupsRouter"));
const Contacts = LoadAsync(() => import(/* webpackChunkName: "contacts" */ "Pages/Project/Contacts/Contacts"));
const Calendar = LoadAsync(() => import(/* webpackChunkName: "calendar" */ "Pages/Project/Calendar/Calendar"));
const Overview = LoadAsync(() => import(/* webpackChunkName: "overview" */ "Pages/Project/Overview/Overview"));
const Settings = LoadAsync(() => import(/* webpackChunkName: "settings" */ "Pages/Project/Settings/Settings"));
const Workflow = LoadAsync(() => import(/* webpackChunkName: "workflow" */ "Pages/Project/Workflow/Workflow"));
const ViewsRouter = LoadAsync(() => import(/* webpackChunkName: "views" */ "Pages/Project/Views/ViewsRouter"));
import { Documents } from "Pages/Project/Documents/Documents";
import { AnnotationSettings } from "Components/PdfJsViewer/Components/PDFAnnotation";
// const Documents = LoadAsync(() => import(/* webpackChunkName: "documents" */ "Pages/Project/Documents/Documents"));
const Communication = LoadAsync(
  () => import(/* webpackChunkName: "communication" */ "Pages/Project/Communication/Communication")
);
const Investigations = LoadAsync(
  () => import(/* webpackChunkName: "investigations" */ "./Investigations/InvestigationsPage")
);
const Tender = LoadAsync(() => import(/* webpackChunkName: "tender" */ "./Tender/TenderRouter"));

const OxIntegrationRouter = LoadAsync(
  () => import(/* webpackChunkName: "ox-integration" */ "Pages/Project/OxIntegration/OxIntegrationRouter")
);

const ProjectManagementRouter = LoadAsync(
  () => import(/* webpackChunkName: "prmanagement" */ "Pages/Project/ProjectManagement/ProjectManagementRouter")
);
const BIMRouter = LoadAsync(() => import(/* webpackChunkName: "bim" */ "Pages/Project/BIM/BIMRouter"));

const DefaultProjectRedirect: React.FC<{ project: ProjectItemUI }> = ({ project }) => {
  const starterPath = project ? getStarterModulePath(project.startWith, project.modules) : null;
  const fallback = "documents/docs";
  return <Navigate to={`/project/${project?.id}/${starterPath || fallback}`} replace />;
};

interface ProjectRouterProps extends WithRouterProps, WithTranslation, WithModal {}

export const ProjectRouter = withTranslation(I18N_NS.PROJECT)(
  withRouter(
    withModal(
      observer(
        class extends React.Component<
          ProjectRouterProps,
          { isProjectInitialized: boolean; isProjectAccessLoaded: boolean }
        > {
          unsubscribeFromHistory;

          constructor(props: ProjectRouterProps) {
            super(props);
            this.state = {
              isProjectInitialized: false,
              isProjectAccessLoaded: false,
            };
          }

          /**
           * Add new project routes here.
           */
          get routes() {
            const routesConfig = [
              {
                path: "overview/*",
                component: Overview,
                pageName: "Overview",
              },
              {
                path: "sta/callback",
                component: StaCallback,
                pageName: "STA",
              },
              {
                path: "documents/*",
                component: Documents,
                pageName: "Documents",
                iconName: "folder-open",
              },
              {
                path: "views/*",
                component: ViewsRouter,
                pageName: "Views",
                iconName: "filter",
              },
              {
                path: "email/*",
                backendName: "MAIL",
                component: Communication,
                pageName: "Communication",
                iconName: "envelope",
              },
              {
                path: "calendar/*",
                backendName: "CALENDAR",
                component: Calendar,
                iconName: "calendar",
                pageName: "Calendar",
              },
              {
                path: "tasks/*",
                backendName: "TASKS",
                component: TasksRouter,
                iconName: "tasks",
                pageName: "Tasks",
              },
              {
                path: "bim/*",
                backendName: "BIM_ISSUES",
                component: BIMRouter,
                iconName: "cube",
                pageName: "BIM",
              },
              {
                path: "workflows/*",
                backendName: "WORKFLOW",
                component: Workflow,
                iconName: "code-commit",
                pageName: "Workflows",
              },
              {
                path: "qa/*",
                backendName: "QA",
                component: QA,
                iconName: "comments",
                pageName: "Q&A",
              },
              {
                path: "investigations/*",
                backendName: "INVESTIGATIONS",
                component: Investigations,
                pageName: "Investigations",
                iconName: "puzzle-piece",
              },
              {
                path: "tenders/*",
                backendName: "TENDER_MODERN",
                component: Tender,
                pageName: "Calls for tenders",
                iconName: "gavel",
              },
              {
                path: "constructions/*",
                backendName: "CONSTRUCTIONLOG",
                component: OxIntegrationRouter,
                pageName: "Construction site",
                iconName: "user-hard-hat",
              },
              {
                path: "project-management/*",
                backendName: "PROJECT_MANAGEMENT",
                component: ProjectManagementRouter,
                pageName: "Project management",
              },
              {
                path: "values/*",
                backendName: "VALUE_TABLE",
                component: ObjectsAndData,
                pageName: "Objects and data",
              },
            ];
            return routesConfig;
          }

          secondaryRoutes = () => {
            const routes = [];
            routes.push({
              path: "groups/*",
              component: GroupsRouter,
              pageName: "Groups",
              iconName: "users",
            });

            routes.push({
              path: "reports/*",
              component: Reports,
              pageName: "Reports",
              iconName: "chart-line",
            });

            if (projectsDataStore.isAdmin) {
              routes.push({
                path: "settings/*",
                component: Settings,
                pageName: "Settings",
                iconName: "cog",
              });
            }

            routes.push({
              path: "contacts/*",
              component: Contacts,
              pageName: "Contacts",
              iconName: "user",
            });
            return routes;
          };

          componentDidMount() {
            this.initProjectRouter();
            // Subscribe to history to display projectName in document.title in project routes
            // this.unsubscribeFromHistory = history.listen(this.handleLocationChange);
            // this.handleLocationChange(history.location);
          }

          // When entering a project from the dashboard, currentProject may already
          // exist from the cached project list (beside others, without tenderModuleAccessLevels). componentDidUpdate
          // must not call initProject() until getCurrentProject(true, true) has finished — otherwise
          // child routes (e.g. CFT/TendersList) mount too early (and skip loading trades).
          // Also see PAVE-5221.
          componentDidUpdate() {
            if (
              this.state.isProjectAccessLoaded &&
              !this.state.isProjectInitialized &&
              !projectsDataStore.showDisclaimer &&
              !projectsDataStore.verificationRequired
            ) {
              this.initProject();
            }
          }

          // handleLocationChange = (location) => {
          //   if (location.pathname.indexOf('project') === -1) {
          //     this.clearAll();
          //   }
          // }

          componentWillUnmount() {
            this.clearAll();
          }

          clearAll = () => {
            // !order of functions is crucial
            titleStore.setProjectName("");
            headerStore.resetLogo();
            clearAllStores();
            projectsDataStore.resetProjectId();
          };

          get isUserGuest() {
            return userInfoStore.role === "GUEST";
          }

          initProjectRouter = async () => {
            console.log("initProjectRouter");
            const { navigate, params } = this.props;
            projectsDataStore.setProjectId(+params.projectId);
            AnnotationSettings.setupFromLocalStorage();
            try {
              await projectsDataStore.getCurrentProject(true, true);
              this.setState({ isProjectAccessLoaded: true });
              if (!projectsDataStore.showDisclaimer && !projectsDataStore.verificationRequired) {
                await this.initProject();
              }
            } catch (err) {
              if (err.response && err.response.status === 409) {
                switch (err.response.data.code) {
                  case 1021: // Disclaimer needs to be signed first
                  case 1064: // Activation code for Secure Cloud Project is required
                    projectsDataStore.setVerificationIsRequired();
                    break;
                  default:
                    throw err;
                }
              } else {
                navigate("/dashboard");
                throw err;
              }
            }
          };

          initProject = async () => {
            try {
              if (
                headerStore.state !== "LOADING" &&
                projectsDataStore.currentProject &&
                projectsDataStore.currentProject.customLogo
              ) {
                await headerStore.setLogo(projectsDataStore.projectId);
              }
              if (contactsStore.state === "RAW") {
                await contactsStore.fetch();
              }
              if (groupListStore.state === "RAW") {
                await groupListStore.fetch();
              }
              if (projectsDataStore.printJobSettings) {
                printJobSettingsStore.isUserAlowToPrint();
              }
              if (projectsDataStore.currentProject) {
                await sidebarStore.setSubMenusForCurrentProject();
              }
              this.setState({ isProjectInitialized: true });
            } catch (err) {
              throw err;
            }
          };

          render() {
            const { isProjectInitialized } = this.state;
            if (projectsDataStore.verificationRequired === true) {
              return <UserVerification projectId={projectsDataStore.projectId} onSuccess={this.initProjectRouter} />;
            } else if (projectsDataStore.passwordRequired === true) {
              return <ConfirmPassword onSuccess={() => projectsDataStore.getCurrentProject(true)} />;
            } else if (projectsDataStore.showDisclaimer) {
              return <Disclaimer />;
            } else {
              if (projectsDataStore.currentProject) {
                const layoutClassName = classNames("project-layout", {
                  guest: this.isUserGuest,
                  archived: projectsDataStore.isArchived,
                });

                return (
                  <ErrorBoundary>
                    <WebDAVModal />
                    <FileDownload />
                    <TaskAccessRightsModal />
                    <Layout className={layoutClassName}>
                      {!this.isUserGuest && isProjectInitialized && (
                        <Sidebar currentProject={projectsDataStore.currentProject} />
                      )}
                      <Layout.Content className="project-content flex">
                        {!this.state.isProjectInitialized ? (
                          <Loader />
                        ) : (
                          <SwitchWith404>
                            {this.routes.map(({ path, pageName, component: Component }) => (
                              <Route
                                key={path}
                                element={
                                  <RouteWithTitle pageName={pageName}>
                                    <Component />
                                  </RouteWithTitle>
                                }
                                path={path}
                              />
                            ))}
                            {this.secondaryRoutes().map(({ path, pageName, component: Component }) => (
                              <Route
                                key={path}
                                element={
                                  <RouteWithTitle pageName={pageName}>
                                    <Component />
                                  </RouteWithTitle>
                                }
                                path={path}
                              />
                            ))}
                            <Route
                              path="*"
                              element={<DefaultProjectRedirect project={projectsDataStore.currentProject} />}
                            />
                          </SwitchWith404>
                        )}
                      </Layout.Content>
                    </Layout>
                  </ErrorBoundary>
                );
              } else {
                return null;
              }
            }
          }
        }
      )
    )
  )
);
